import {
    Button,
    Dropdown,
    Input,
    Layout,
    MenuProps,
    Popconfirm,
    Select,
    Space,
    Tooltip,
} from "antd";
import { InfoCircleOutlined, TeamOutlined } from "@ant-design/icons";
import Table, { ColumnProps } from "antd/lib/table";
import { observer } from "mobx-react-lite";
import React from "react";
import { safeStringLocaleCompare, Utils } from "../../common/misc/Utils";
import { ProjectsStore } from "../../common/stores";
import ActionDefinitionStore from "../stores/ActionDefinitionStore";
import { ActionDefinition } from "../types/Actions";
import ActionDefinitionCreateEditDialog from "./ActionDefinitionCreateEditDialog";
import ImportActionDefDialog from "./ImportActionDefDialog";
import ManageActionRolesDialog from "./ManageActionRolesDialog";
import { DndProvider } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import { DragableBodyRow } from "../../common/components/TableRowDragAndDropRenderer";
const { Content } = Layout;

type Props = {
    store: ActionDefinitionStore;
    projectsStore: ProjectsStore;
};

const ActionDefinitionsList: React.FC<Props> = ({ store, projectsStore }) => {
    const [projectSelected, setProjectSelected] =
        React.useState<boolean>(false);
    const [isImportDefinitionVisible, setIsImportDefinitionVisible] =
        React.useState(false);
    const [sortingApplied, setSortingApplied] = React.useState<boolean>(false);
    const hasSelectedProject = Boolean(store.selectedProjectId);
    const hasActions = store.actionDefinitions.length > 0;
    const shouldShowEmptyState =
        projectSelected &&
        hasSelectedProject &&
        !store.isLoading &&
        !hasActions;

    React.useEffect(() => {
        store.loadActionTypes();
    }, []);

    const moreActionsMenu: MenuProps = {
        items: [
            {
                key: "manage-action-roles",
                icon: <TeamOutlined />,
                label: "Manage action roles",
                onClick: () => {
                    void store.openActionRolesDialog();
                },
                disabled: !store.selectedProjectId,
            },
        ],
    };

    const renderAccess = (record: ActionDefinition) => {
        const roleNames = (record.permissions?.roles ?? []).map((role) =>
            store.getActionRoleName(role),
        );

        if (roleNames.length > 0) {
            const visibleRoles = roleNames.slice(0, 2);
            const hiddenCount = roleNames.length - visibleRoles.length;

            return (
                <div className="action-access-list">
                    {visibleRoles.map((role) => (
                        <span
                            key={role}
                            className="action-access-badge action-access-badge-role"
                        >
                            {role}
                        </span>
                    ))}
                    {hiddenCount > 0 && (
                        <Tooltip title={roleNames.slice(2).join(", ")}>
                            <span className="action-access-badge action-access-badge-overflow">
                                +{hiddenCount}
                            </span>
                        </Tooltip>
                    )}
                </div>
            );
        }

        if (record.permissions?.admin) {
            return (
                <span className="action-access-badge action-access-badge-admin">
                    Project Admins
                </span>
            );
        }

        return (
            <span className="action-access-badge action-access-badge-project">
                Any project user
            </span>
        );
    };

    const columns: ColumnProps<ActionDefinition>[] = [
        {
            title: "Action label",
            dataIndex: "title",
            key: "title",
            render: (text: string, record: ActionDefinition) => (
                <span data-id-cells="name">{record.title}</span>
            ),
            sorter: (a: ActionDefinition, b: ActionDefinition) =>
                safeStringLocaleCompare(b.title, a.title),
        },
        {
            title: "System name",
            dataIndex: "name",
            key: "name",
            render: (text: string, record: ActionDefinition) => (
                <span data-id-cells="name" className="text-muted">
                    {record.name}
                </span>
            ),
            sorter: (a: ActionDefinition, b: ActionDefinition) =>
                safeStringLocaleCompare(b.name, a.name),
        },
        {
            title: "Type",
            dataIndex: "actionType",
            key: "actionType",
            render: (text: string, record: ActionDefinition) => (
                <span data-id-cells="name">
                    {
                        store.actionTypes.find(
                            (x) => x.name === record.actionType,
                        )?.title
                    }
                </span>
            ),
            sorter: (a: ActionDefinition, b: ActionDefinition) => {
                const titleA = store.actionTypes.find(
                    (x) => x.name === a.actionType,
                )?.title;
                const titleB = store.actionTypes.find(
                    (x) => x.name === b.actionType,
                )?.title;
                return safeStringLocaleCompare(titleB, titleA);
            },
        },
        {
            title: "Available on",
            dataIndex: "isAttachment",
            key: "isAttachment",
            render: (_, record: ActionDefinition) => (
                <span
                    className={`action-access-badge action-availability-badge ${
                        record.isAttachment
                            ? "action-availability-badge-attachment"
                            : "action-availability-badge-task"
                    }`}
                >
                    {record.isAttachment ? "Attachment" : "Task"}
                </span>
            ),
            sorter: (a: ActionDefinition, b: ActionDefinition) => {
                if (a.isAttachment === b.isAttachment) return 0;
                return a.isAttachment ? -1 : 1;
            },
        },
        {
            title: (
                <Space size={6}>
                    <span>Access</span>
                    <Tooltip title="Who can run this action">
                        <InfoCircleOutlined className="action-access-title-icon" />
                    </Tooltip>
                </Space>
            ),
            key: "access",
            render: (_, record: ActionDefinition) => renderAccess(record),
        },
        {
            title: "Confirmation",
            dataIndex: "isConfirmationRequired",
            key: "isConfirmationRequired",
            render: (_, record: ActionDefinition) =>
                record.isConfirmationRequired ? (
                    <span className="text-muted">Required</span>
                ) : null,
            sorter: (a: ActionDefinition, b: ActionDefinition) => {
                if (a.isConfirmationRequired === b.isConfirmationRequired)
                    return 0;
                return a.isConfirmationRequired ? -1 : 1;
            },
        },
        {
            title: "Actions",
            width: 200,
            render: (text: string, record: ActionDefinition) => (
                <div className="row-actions-wrapper">
                    <Button
                        data-id-cells="Edit action definition"
                        title="Rename"
                        size="small"
                        type="link"
                        onClick={() => {
                            store.selectActionDefinition(record);
                        }}
                    >
                        <Tooltip
                            title="Edit action definition"
                            placement="bottom"
                        >
                            <i className="alpha-icon md table-action-edit" />
                        </Tooltip>
                    </Button>
                    <Button
                        data-id-cells="Export action"
                        title="Export"
                        size="small"
                        type="link"
                        onClick={() => {
                            void store.exportActionDefinition(record.id);
                        }}
                    >
                        <Tooltip
                            title="Export action definition"
                            placement="bottom"
                        >
                            <i className="alpha-icon md export" />
                        </Tooltip>
                    </Button>
                    <Popconfirm
                        id="data-id-popconfirm-box"
                        title="Are you sure you want to delete this action definition?"
                        onConfirm={() => {
                            store.deleteActionDefinition(record.id);
                        }}
                        placement="topRight"
                    >
                        <Button
                            data-id-cells="Delete action definition"
                            title="Delete"
                            size="small"
                            type="link"
                        >
                            <Tooltip
                                title="Delete action definition"
                                placement="bottom"
                            >
                                <i className="alpha-icon md table-action-delete" />
                            </Tooltip>
                        </Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const dragAndDropBody = {
        body: {
            row: DragableBodyRow,
        },
    };

    const moveRow = (
        dragIndex: number,
        dropIndex: number,
        draggedRecord: ActionDefinition,
    ) => {
        store.updateListOrder(draggedRecord.id, dragIndex, dropIndex);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleTableChange = (pag: any, filt: any, sorter: any) => {
        setSortingApplied(!!sorter.column);
    };

    const handleEnterPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        store.setSearchString(event.currentTarget.value);
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Layout className="screen-size tasks-types-layout layout-with-table">
                <div className="header-wrapper">
                    <div className="title-wrapper">
                        <div className="header-title">Action definitions</div>
                    </div>
                    <Dropdown
                        menu={moreActionsMenu}
                        trigger={["click"]}
                        overlayClassName="alpha-dropdown-menu"
                    >
                        <Button
                            key={1}
                            disabled={!store.selectedProjectId}
                            size="large"
                            style={{ marginRight: 8 }}
                        >
                            More actions
                        </Button>
                    </Dropdown>
                    <Button
                        key={2}
                        disabled={!store.selectedProjectId}
                        data-id="button-add-type"
                        style={{ marginRight: 8 }}
                        type="primary"
                        size="large"
                        onClick={() => {
                            setIsImportDefinitionVisible(true);
                        }}
                    >
                        Import definition
                    </Button>
                    <Button
                        key={3}
                        disabled={!store.selectedProjectId}
                        data-id="button-add-definition"
                        type="primary"
                        size="large"
                        onClick={() => {
                            store.setNewDefinitionDialogVisible(true);
                        }}
                    >
                        Add definition
                    </Button>
                </div>
                <ActionDefinitionCreateEditDialog store={store} />
                <ManageActionRolesDialog store={store} />
                <ImportActionDefDialog
                    store={store}
                    isDialogVisible={isImportDefinitionVisible}
                    closeDialog={() => setIsImportDefinitionVisible(false)}
                />
                <Layout>
                    <Content>
                        <div className="projects-list-container">
                            <Select
                                options={
                                    projectsStore.administrableProjects?.map(
                                        (p) => ({ label: p.name, value: p.id }),
                                    ) || []
                                }
                                style={{ width: 220 }}
                                value={store.selectedProjectId || undefined}
                                placeholder="Select project"
                                onChange={(v) => {
                                    setProjectSelected(true);
                                    store.setSelectedProjectId(v);
                                }}
                                showSearch
                                filterOption={Utils.filterOption}
                            />
                            {!shouldShowEmptyState ? (
                                <Input
                                    data-id="search area"
                                    placeholder="Search..."
                                    onPressEnter={handleEnterPress}
                                    onClear={() => store.setSearchString("")}
                                    maxLength={150}
                                    style={{ width: 200, marginLeft: "auto" }}
                                    prefix={
                                        <i
                                            className="alpha-icon xs table-search"
                                            style={{ marginRight: 10 }}
                                        />
                                    }
                                    allowClear
                                />
                            ) : null}
                        </div>
                        {shouldShowEmptyState ? (
                            <div className="task-types-empty-state">
                                <div className="task-types-empty-state-title">
                                    No actions available
                                </div>
                                <div className="task-types-empty-state-description">
                                    Actions are optional. Add an action
                                    definition only if this project needs
                                    reusable buttons, confirmations, or custom
                                    task interactions.
                                </div>
                                <Button
                                    data-id="button-create-action-definition"
                                    type="primary"
                                    size="large"
                                    onClick={() => {
                                        store.setNewDefinitionDialogVisible(
                                            true,
                                        );
                                    }}
                                >
                                    Add definition
                                </Button>
                            </div>
                        ) : (
                            <Table<ActionDefinition>
                                data-id="action-definitions-list"
                                className="alpha-table action-definitions"
                                columns={columns}
                                loading={store.isLoading}
                                dataSource={
                                    projectSelected
                                        ? store.filteredActionDefinitions
                                              .slice()
                                              .sort(
                                                  (a, b) =>
                                                      a.position - b.position,
                                              )
                                        : []
                                }
                                rowKey={(r) => r.id}
                                pagination={false}
                                components={
                                    store.searchString || sortingApplied
                                        ? undefined
                                        : dragAndDropBody
                                }
                                onRow={(record, index) =>
                                    ({
                                        index,
                                        record,
                                        moveRow: moveRow,
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    }) as any
                                }
                                onChange={handleTableChange}
                                rowSelection={{}}
                                scroll={{ y: "calc(100vh - 222px)" }}
                            />
                        )}
                    </Content>
                </Layout>
            </Layout>
        </DndProvider>
    );
};

export default observer(ActionDefinitionsList);
